import { useState } from 'react';
import { Link } from 'react-router-dom';

interface NavbarProps {
  scrolled: boolean;
}

export default function Navbar({ scrolled }: NavbarProps) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 transition-all duration-300 bg-white shadow-md">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-24">
          {/* Logo */}
          <div className="flex items-center">
            <Link to="/" className="flex items-center cursor-pointer">
              <img
                src="https://storage.readdy-site.link/project_files/70b7a5e7-167c-457e-ada5-3921d7ed109f/d7142fa2-ba05-4d52-830b-5f6de0d95291_city_to_city_logo.png?v=e696fa2f2c1d4156b71769d9a75c094b"
                alt="City to City Moving Company"
                className="h-16 w-auto object-contain"
              />
            </Link>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-8">
            <Link to="/" className="text-sm font-semibold transition-colors text-slate-700 hover:text-orange-500 cursor-pointer">
              Home
            </Link>
            <Link to="/services" className="text-sm font-semibold transition-colors text-slate-700 hover:text-orange-500 cursor-pointer">
              Services
            </Link>
            <Link to="/about" className="text-sm font-semibold transition-colors text-slate-700 hover:text-orange-500 cursor-pointer">
              About Us
            </Link>
            <Link to="/locations" className="text-sm font-semibold transition-colors text-slate-700 hover:text-orange-500 cursor-pointer">
              Locations
            </Link>
          </div>

          {/* Social Icons & CTA */}
          <div className="hidden lg:flex items-center gap-4">
            {/* Social Icons */}
            <div className="flex items-center gap-3 mr-2">
              <a 
                href="https://www.facebook.com" 
                target="_blank"
                rel="noopener noreferrer"
                className="w-9 h-9 flex items-center justify-center rounded-full transition-all hover:scale-110 bg-slate-100 text-slate-700 hover:bg-orange-500 hover:text-white"
              >
                <i className="ri-facebook-fill text-lg"></i>
              </a>
              <a 
                href="https://www.instagram.com" 
                target="_blank"
                rel="noopener noreferrer"
                className="w-9 h-9 flex items-center justify-center rounded-full transition-all hover:scale-110 bg-slate-100 text-slate-700 hover:bg-orange-500 hover:text-white"
              >
                <i className="ri-instagram-fill text-lg"></i>
              </a>
              <a 
                href="https://www.twitter.com" 
                target="_blank"
                rel="noopener noreferrer"
                className="w-9 h-9 flex items-center justify-center rounded-full transition-all hover:scale-110 bg-slate-100 text-slate-700 hover:bg-orange-500 hover:text-white"
              >
                <i className="ri-twitter-x-fill text-lg"></i>
              </a>
            </div>

            {/* Phone */}
            <a 
              href="tel:7606257767" 
              className="flex items-center gap-2 px-4 py-2.5 rounded-xl font-semibold text-sm transition-all whitespace-nowrap text-slate-700 hover:bg-slate-100"
            >
              <i className="ri-phone-line text-lg"></i>
              (760) 625-7767
            </a>

            {/* CTA Button */}
            <a 
              href="#quote-form" 
              className="bg-gradient-to-r from-orange-500 to-red-500 text-white px-6 py-3 rounded-xl font-bold text-sm hover:shadow-xl hover:scale-105 transition-all whitespace-nowrap"
            >
              Get Free Quote
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="lg:hidden p-2 rounded-xl transition-colors text-slate-700 hover:bg-slate-100"
          >
            <i className={`text-2xl ${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="lg:hidden bg-white border-t border-gray-200 shadow-xl">
          <div className="px-4 py-4 space-y-3">
            <Link to="/" className="block py-2 text-slate-700 font-semibold hover:text-orange-500 cursor-pointer" onClick={() => setMobileMenuOpen(false)}>
              Home
            </Link>
            <Link to="/services" className="block py-2 text-slate-700 font-semibold hover:text-orange-500 cursor-pointer" onClick={() => setMobileMenuOpen(false)}>
              Services
            </Link>
            <Link to="/about" className="block py-2 text-slate-700 font-semibold hover:text-orange-500 cursor-pointer" onClick={() => setMobileMenuOpen(false)}>
              About Us
            </Link>
            <Link to="/locations" className="block py-2 text-slate-700 font-semibold hover:text-orange-500 cursor-pointer" onClick={() => setMobileMenuOpen(false)}>
              Locations
            </Link>

            {/* Social Icons Mobile */}
            <div className="flex items-center gap-3 pt-3 border-t border-gray-200">
              <a 
                href="https://www.facebook.com" 
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 flex items-center justify-center rounded-full bg-slate-100 text-slate-700 hover:bg-orange-500 hover:text-white transition-all"
              >
                <i className="ri-facebook-fill text-lg"></i>
              </a>
              <a 
                href="https://www.instagram.com" 
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 flex items-center justify-center rounded-full bg-slate-100 text-slate-700 hover:bg-orange-500 hover:text-white transition-all"
              >
                <i className="ri-instagram-fill text-lg"></i>
              </a>
              <a 
                href="https://www.twitter.com" 
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 flex items-center justify-center rounded-full bg-slate-100 text-slate-700 hover:bg-orange-500 hover:text-white transition-all"
              >
                <i className="ri-twitter-x-fill text-lg"></i>
              </a>
            </div>

            <div className="pt-3 border-t border-gray-200">
              <a href="tel:7606257767" className="block w-full bg-slate-800 text-white py-3 px-4 rounded-xl font-semibold text-center mb-2 whitespace-nowrap hover:bg-slate-900 transition-colors">
                <i className="ri-phone-line mr-2"></i>Call (760) 625-7767
              </a>
              <a href="#quote-form" className="block w-full bg-gradient-to-r from-orange-500 to-red-500 text-white py-3 px-4 rounded-xl font-bold text-center whitespace-nowrap hover:shadow-lg transition-all" onClick={() => setMobileMenuOpen(false)}>
                Get Free Quote
              </a>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}
